<template>
  <VueFinalModal
    v-model="model"
    v-bind="$props as any"
    :modal-id="modalId"
    :focus-trap="false"
    :teleport-to="teleport"
    :display-directive="displayDirective"
    :click-to-close="clickToClose"
    overlay-class="!bg-transparent z-[1]"
    overlay-transition="vfm-fade"
    :content-transition="$device.isMobile ? 'vfm-slide-down' : 'vfm-fade'"
    class="flex justify-center bg-gray-900/90 dark:bg-gray-900/80 backdrop-blur-sm items-start py-12 relative overflow-x-auto"
    :content-class="`mx-auto sm:bottom-auto bottom-0 sm:relative fixed w-full rounded-t-xl sm:rounded-xl bg-white ring ring-white dark:ring-gray-900 dark:bg-gray-900 dark:border dark:border-gray-700 shadow-2xl sm:shadow-xl ${modalSizeClass} max-h-[calc(100vh-60px)] sm:max-h-none overflow-y-auto sm:overflow-y-visible`"
    @opened="$emit('opened')"
  >
    <div
      v-if="title || $slots.header"
      class="bg-gray-100 bg-gradient-to-b dark:bg-gray-800 rounded-t-xl border-t border-t-white dark:border-t-gray-800 flex items-center justify-between py-3 px-6"
    >
      <span class="text-[1.08rem] font-bold">
        <slot name="header">{{ truncate(title, 80) }}</slot>
      </span>
      <slot name="close-modal">
        <UButton
          v-if="canBeClosed"
          icon="i-fluent:dismiss-24-filled"
          class="hover:opacity-50 duration-300"
          color="black"
          variant="link"
          @click="modalProvide.close()"
        ></UButton>
      </slot>
    </div>
    <div :class="{ 'p-0': !bodyPadded, 'p-6': bodyPadded }">
      <slot />
    </div>
  </VueFinalModal>
</template>

<script lang="ts" setup>
import type { VueFinalModalEmits } from 'vue-final-modal'
import { VueFinalModal } from 'vue-final-modal'

const props = withDefaults(
  defineProps<{
    bodyPadded?: boolean
    canBeClosed?: boolean
    clickToClose?: boolean
    escToClose?: boolean
    modalId?: number | string
    size?: '2xs' | 'lg' | 'md' | 'sm' | 'xl' | 'xs'
    title?: string
  }>(),
  {
    bodyPadded: true,
    canBeClosed: true,
    clickToClose: false,
    escToClose: true,
    size: 'md',
  },
)

defineEmits<VueFinalModalEmits>()

const model = defineModel<boolean>()

const { $useVfm } = useNuxtApp()

const modalId = props.modalId ?? useId()
const vfm = $useVfm()

// the modal keeps displaying an empty teleport in vitest
// so we have to disable it for tests
// if someone has a better idea for this, please let me know
const teleport = process.env.TEST ? false : 'body'
const displayDirective = process.env.TEST ? 'visible' : 'if'

const modalSizeClass = computed(() => {
  switch (props.size) {
    case '2xs':
      return 'max-w-sm'
    case 'lg':
      return 'max-w-4xl'
    case 'md':
      return 'max-w-2xl'
    case 'sm':
      return 'max-w-lg'
    case 'xl':
      return 'max-w-7xl'
    case 'xs':
      return 'max-w-md'
    default:
      return 'max-w-2xl'
  }
})

const modalProvide = {
  close: () => vfm.close(modalId!),
}

provide('modal', modalProvide)
</script>
